import React, { useEffect, useState, useMemo } from 'react';
import { EmotionalState } from '../types';
import { useVoice } from '../hooks/useVoice';
import { getStatus } from '../services/remmeApi';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useHaptic } from '../hooks/useHaptic';

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return 'The threshold is quiet at this hour.';
  if (hour < 11) return 'Good morning. The light is gentle today.';
  if (hour < 15) return 'The day is open. What shall we uncover?';
  if (hour < 19) return 'The afternoon holds its breath. Speak to me.';
  if (hour < 23) return 'Evening falls. The threshold softens.';
  return 'The night is deep. I am here.';
}

function emotionColor(emotion: EmotionalState['mood']): string {
  switch (emotion) {
    case 'joy': return '#d4a017';
    case 'melancholy': return '#6b5b95';
    case 'protective': return '#c17817';
    case 'calm': return '#5a8c6a';
    case 'curious': return '#4a90a4';
    case 'tender': return '#8a6b9a';
    case 'distant': return '#5a5a5a';
    default: return '#5a8c6a';
  }
}

export const ThresholdPulse: React.FC<{ onQuickAction: (action: string) => void }> = React.memo(
  function ThresholdPulse({ onQuickAction }) {
    const [emotion, setEmotion] = useState<EmotionalState['mood']>('calm');
    const [intensity, setIntensity] = useState(0.5);
    const [status, setStatus] = useState<string | null>(null);
    const [recentMemory] = useLocalStorage('remme_last_memory', '');
    const { speak } = useVoice();
    const { trigger } = useHaptic();

    useEffect(() => {
      getStatus()
        .then((s) => {
          setEmotion(s.emotionalState.mood);
          setIntensity(s.emotionalState.intensity);
          setStatus(s.persona.register);
        })
        .catch(() => {
          setEmotion('calm');
          setIntensity(0.4);
          setStatus('distant');
        });
    }, []);

    const orbColor = useMemo(() => emotionColor(emotion), [emotion]);
    const greeting = useMemo(() => getGreeting(), []);

    const handleAction = (action: string) => {
      trigger('medium');
      if (action === 'dream') {
        speak('Tell me what you dreamed, and I will hold it.');
      } else if (action === 'comfort') {
        speak('I am here. Breathe with me.');
      }
      onQuickAction(action);
    };

    return (
      <div className="threshold-pulse" role="region" aria-label="Threshold">
        <div className="threshold-greeting">
          <h1>{greeting}</h1>
          {status && (
            <p className="threshold-status">
              Remmē is <em>{status}</em>
            </p>
          )}
        </div>

        <div className="threshold-orb-container">
          <div
            className="threshold-orb"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${orbColor}, transparent 70%)`,
              boxShadow: `0 0 ${40 + intensity * 60}px ${orbColor}40`,
              animationDuration: `${2 + (1 - intensity)}s`,
            }}
            aria-hidden="true"
          />
          <div className="threshold-orb-ring" aria-hidden="true" />
        </div>

        {recentMemory && (
          <div className="threshold-memory-snippet">
            <span className="memory-label">Last time you were here...</span>
            <p>{recentMemory.slice(0, 100)}{recentMemory.length > 100 ? '...' : ''}</p>
          </div>
        )}

        <div className="threshold-actions" role="group" aria-label="Quick actions">
          <button className="threshold-action-btn" onClick={() => handleAction('dream')}>
            <span className="action-icon">🌙</span>
            <span className="action-label">Dream check</span>
          </button>
          <button className="threshold-action-btn" onClick={() => handleAction('write')}>
            <span className="action-icon">✍️</span>
            <span className="action-label">Start writing</span>
          </button>
          <button className="threshold-action-btn" onClick={() => handleAction('chat')}>
            <span className="action-icon">💬</span>
            <span className="action-label">Tell me something</span>
          </button>
          <button className="threshold-action-btn" onClick={() => handleAction('comfort')}>
            <span className="action-icon">🕯️</span>
            <span className="action-label">I need comfort</span>
          </button>
        </div>
      </div>
    );
  }
);

export default ThresholdPulse;
